import React, { useState } from 'react';
import { ArrowRight, Scan, CheckCircle, AlertTriangle, FileText, Loader2, Shield, ExternalLink } from 'lucide-react';
import Layout from '../../layouts/Layout';
import apiClient from '../../helpers/apiClient';
import NotificationPopup from '../../components/NotificationPopup';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link } from 'react-router-dom';

const EAAComplianceScannerPage = () => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        websiteUrl: ''
    });

    const [isScanning, setIsScanning] = useState(false);
    const [scanResults, setScanResults] = useState(null);
    const [scanId, setScanId] = useState(null);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [notification, setNotification] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const showNotification = (message, type = 'success', duration = 5000) => {
        setNotification({ message, type });
        setTimeout(() => {
            setNotification(null);
        }, duration);
    };

    const hideNotification = () => {
        setNotification(null);
    };

    const validateUrl = (url) => {
        try {
            const urlObj = new URL(url);
            return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
        } catch {
            return false;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateUrl(formData.websiteUrl)) {
            showNotification('Please enter a valid website URL including http:// or https://', 'error');
            return;
        }

        setIsScanning(true);
        setScanResults(null);

        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available');
            setIsScanning(false);
            return;
        }

        try {
            const token = await executeRecaptcha('eaa_scanner');
            
            const response = await apiClient.scanEAACompliance({
                ...formData,
                recaptchaToken: token
            });

            if (response.success) {
                setScanResults(response.results);
                setScanId(response.scan_id);
                showNotification('Scan completed successfully!', 'success');
            } else {
                throw new Error(response.error || 'Scan failed');
            }
        } catch (error) {
            console.error('Scan error:', error);
            showNotification(
                `Error: ${error.message}`,
                'error',
                8000
            );
        } finally {
            setIsScanning(false);
        }
    };

    const handleDownloadPDF = async () => {
        if (!scanId || !executeRecaptcha) {
            showNotification('Unable to generate PDF. Please try scanning again.', 'error');
            return;
        }

        setIsGeneratingPDF(true);

        try {
            const token = await executeRecaptcha('pdf_download');
            
            const pdfBlob = await apiClient.generatePDFReport(scanId, token);
            
            // Create download link
            const url = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            
            // Generate filename
            const domain = new URL(scanResults.url).hostname;
            const filename = `EAA_Compliance_Report_${domain.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
            link.download = filename;
            
            // Trigger download
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up
            window.URL.revokeObjectURL(url);
            
            showNotification('PDF report downloaded successfully!', 'success');
            
        } catch (error) {
            console.error('PDF generation error:', error);
            showNotification(
                `Error generating PDF: ${error.message}`,
                'error',
                8000
            );
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'critical':
                return 'text-red-600 bg-red-50 border-red-200';
            case 'serious':
                return 'text-orange-600 bg-orange-50 border-orange-200';
            case 'moderate':
                return 'text-yellow-600 bg-yellow-50 border-yellow-200';
            case 'minor':
                return 'text-blue-600 bg-blue-50 border-blue-200';
            default:
                return 'text-slate-600 bg-slate-50 border-slate-200';
        }
    };

    const getComplianceScore = (violations) => {
        if (!violations || violations.length === 0) return 100;
        
        const weights = {
            critical: 25,
            serious: 15,
            moderate: 10,
            minor: 5
        };
        
        let totalDeductions = 0;
        violations.forEach(violation => {
            const weight = weights[violation.impact] || 5;
            totalDeductions += weight;
        });
        
        return Math.max(0, 100 - totalDeductions);
    };

    return (
        <Layout
            title="Free EAA Compliance Scanner"
            description="Instantly scan your website for European Accessibility Act compliance. Get a free accessibility audit report with actionable recommendations."
            keywords="EAA scanner, accessibility checker, WCAG compliance test, free accessibility audit, website accessibility scanner"
            path="/services/eaa-compliance-scanner"
        >
            <div className="min-h-screen bg-white">
                <NotificationPopup notification={notification} hideNotification={hideNotification} />

                {/* Hero Section */}
                <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-slate-50 to-slate-100">
                    <div className="max-w-6xl mx-auto text-center">
                        <div className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-full text-sm font-medium mb-8">
                            <Scan className="w-4 h-4 mr-2" />
                            FREE ACCESSIBILITY SCANNER
                        </div>
                        
                        <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-8 leading-tight">
                            EAA Compliance Scanner
                            <span className="block text-slate-700 mt-2">Test Your Website Now</span>
                        </h1>
                        
                        <p className="text-xl text-slate-600 mb-12 leading-relaxed max-w-4xl mx-auto">
                            Instantly check if your website meets European Accessibility Act requirements. 
                            Get a detailed report with compliance score, violations, and recommendations.
                        </p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-8 border-t border-slate-200 max-w-3xl mx-auto">
                            <div>
                                <div className="text-3xl font-bold text-slate-900">Free</div>
                                <div className="text-slate-600">No cost</div>
                            </div>
                            <div>
                                <div className="text-3xl font-bold text-slate-900">30s</div>
                                <div className="text-slate-600">Scan time</div>
                            </div>
                            <div>
                                <div className="text-3xl font-bold text-slate-900">WCAG</div>
                                <div className="text-slate-600">2.1 AA</div>
                            </div>
                            <div>
                                <div className="text-3xl font-bold text-slate-900">PDF</div>
                                <div className="text-slate-600">Report</div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Scanner Form */}
                <section className="py-24 bg-white">
                    <div className="max-w-4xl mx-auto px-6">
                        {!scanResults ? (
                            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 shadow-lg">
                                <form onSubmit={handleSubmit} className="space-y-8">
                                    <div>
                                        <h2 className="text-2xl font-bold text-slate-900 mb-6">Scan Your Website</h2>
                                        
                                        <div className="space-y-6">
                                            <div>
                                                <label className="block text-slate-900 font-medium mb-2" htmlFor="websiteUrl">
                                                    Website URL *
                                                </label>
                                                <input
                                                    type="url"
                                                    id="websiteUrl"
                                                    name="websiteUrl"
                                                    value={formData.websiteUrl}
                                                    onChange={handleInputChange}
                                                    required
                                                    placeholder="https://example.com"
                                                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-colors"
                                                />
                                                <p className="text-sm text-slate-500 mt-1">
                                                    Enter the full URL including https://
                                                </p>
                                            </div>

                                            <div className="grid md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-slate-900 font-medium mb-2" htmlFor="name">
                                                        Your Name *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="name"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleInputChange}
                                                        required
                                                        className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-colors"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-slate-900 font-medium mb-2" htmlFor="email">
                                                        Email Address *
                                                    </label>
                                                    <input
                                                        type="email"
                                                        id="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleInputChange}
                                                        required
                                                        className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-colors"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-slate-900 font-medium mb-2" htmlFor="company">
                                                    Company Name
                                                </label>
                                                <input
                                                    type="text"
                                                    id="company"
                                                    name="company"
                                                    value={formData.company}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-colors"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="flex items-start space-x-3 cursor-pointer mb-6">
                                            <input
                                                type="checkbox"
                                                checked={agreedToTerms}
                                                onChange={(e) => setAgreedToTerms(e.target.checked)}
                                                required
                                                className="mt-1 w-4 h-4 text-slate-900 border-slate-300 rounded focus:ring-slate-900 focus:ring-2"
                                            />
                                            <span className="text-sm text-slate-600 leading-relaxed">
                                                I agree to receive the scan results via email and understand that this is a basic automated scan. 
                                                For comprehensive compliance assessment, professional consultation is recommended. 
                                                By using this tool, I agree to the{' '}
                                                <Link to="/privacy-policy" className="text-slate-900 underline hover:no-underline">
                                                    Privacy Policy
                                                </Link>.
                                            </span>
                                        </label>

                                        <button
                                            type="submit"
                                            disabled={!executeRecaptcha || isScanning || !agreedToTerms}
                                            className="w-full bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-slate-800 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isScanning ? (
                                                <>
                                                    <Loader2 className="animate-spin mr-2 w-5 h-5" />
                                                    Scanning Website...
                                                </>
                                            ) : (
                                                <>
                                                    Start Free Scan <ArrowRight className="ml-2 w-5 h-5" />
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {/* Results Summary */}
                                <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
                                    <div className="flex items-center justify-between mb-6">
                                        <h2 className="text-2xl font-bold text-slate-900">Scan Results</h2>
                                        <button
                                            onClick={() => {
                                                setScanResults(null);
                                                setScanId(null);
                                                setFormData({
                                                    name: '',
                                                    email: '',
                                                    company: '',
                                                    websiteUrl: ''
                                                });
                                            }}
                                            className="text-slate-600 hover:text-slate-900 font-medium"
                                        >
                                            New Scan
                                        </button>
                                    </div>

                                    <div className="grid md:grid-cols-3 gap-6 mb-8">
                                        <div className="text-center p-6 bg-slate-50 rounded-xl">
                                            <div className="text-4xl font-bold text-slate-900 mb-2">
                                                {getComplianceScore(scanResults.violations)}%
                                            </div>
                                            <div className="text-slate-600">Compliance Score</div>
                                        </div>
                                        <div className="text-center p-6 bg-slate-50 rounded-xl">
                                            <div className="text-4xl font-bold text-red-600 mb-2">
                                                {scanResults.violations?.length || 0}
                                            </div>
                                            <div className="text-slate-600">Issues Found</div>
                                        </div>
                                        <div className="text-center p-6 bg-slate-50 rounded-xl">
                                            <div className="text-4xl font-bold text-green-600 mb-2">
                                                {scanResults.passes?.length || 0}
                                            </div>
                                            <div className="text-slate-600">Tests Passed</div>
                                        </div>
                                    </div>

                                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
                                        <div className="flex items-start">
                                            <AlertTriangle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                                            <div className="text-sm text-amber-800">
                                                <strong>Important:</strong> This automated scan provides a basic assessment of accessibility issues. 
                                                A comprehensive EAA compliance audit requires manual testing and expert review. 
                                                <Link to="/contact" className="underline hover:no-underline ml-1">
                                                    Contact us for professional assessment
                                                </Link>.
                                            </div>
                                        </div>
                                    </div>

                                    {/* Download Report Button */}
                                    <div className="flex justify-center">
                                        <button
                                            onClick={handleDownloadPDF}
                                            disabled={isGeneratingPDF || !scanId}
                                            className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isGeneratingPDF ? (
                                                <>
                                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                    Generating PDF...
                                                </>
                                            ) : (
                                                <>
                                                    <FileText className="w-5 h-5 mr-2" />
                                                    Download Full Report (PDF)
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Violations List */}
                                {scanResults.violations && scanResults.violations.length > 0 && (
                                    <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
                                        <h3 className="text-xl font-bold text-slate-900 mb-6">Accessibility Issues</h3>
                                        <div className="space-y-4">
                                            {scanResults.violations.slice(0, 10).map((violation, index) => (
                                                <div key={index} className={`p-4 rounded-lg border ${getSeverityColor(violation.impact)}`}>
                                                    <div className="flex items-start justify-between mb-2">
                                                        <h4 className="font-semibold text-slate-900">{violation.description}</h4>
                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${getSeverityColor(violation.impact)}`}>
                                                            {violation.impact?.toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-slate-600 mb-2">{violation.help}</p>
                                                    {violation.helpUrl && (
                                                        <a 
                                                            href={violation.helpUrl} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-sm text-blue-600 hover:underline flex items-center"
                                                        >
                                                            Learn more <ExternalLink className="w-3 h-3 ml-1" />
                                                        </a>
                                                    )}
                                                    <div className="mt-2 text-sm text-slate-500">
                                                        Found on {violation.nodes?.length || 0} element(s)
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        {scanResults.violations.length > 10 && (
                                            <p className="text-center text-slate-600 mt-6">
                                                And {scanResults.violations.length - 10} more issues...
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Next Steps */}
                                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
                                    <h3 className="text-xl font-bold text-slate-900 mb-6">Next Steps</h3>
                                    <div className="space-y-4">
                                        <div className="flex items-start">
                                            <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <h4 className="font-semibold text-slate-900 mb-1">Professional Audit</h4>
                                                <p className="text-slate-600">
                                                    Get a comprehensive manual audit including user testing and detailed remediation guidance.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <Shield className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <h4 className="font-semibold text-slate-900 mb-1">Implementation Support</h4>
                                                <p className="text-slate-600">
                                                    Our experts can fix all accessibility issues and ensure full EAA compliance.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <FileText className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <h4 className="font-semibold text-slate-900 mb-1">Compliance Certification</h4>
                                                <p className="text-slate-600">
                                                    Receive official documentation proving your EAA compliance status.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-8 flex flex-col sm:flex-row gap-4">
                                        <Link 
                                            to="/contact" 
                                            className="bg-slate-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-slate-800 transition-colors text-center"
                                        >
                                            Get Professional Audit
                                        </Link>
                                        <Link 
                                            to="/services/eaa-compliance" 
                                            className="border-2 border-slate-300 text-slate-700 px-6 py-3 rounded-lg font-semibold hover:border-slate-400 hover:bg-slate-50 transition-all text-center"
                                        >
                                            Learn About EAA Compliance
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* Info Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">How Our Scanner Works</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Powered by industry-standard accessibility testing engines
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8">
                            <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center">
                                <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mx-auto mb-6">
                                    <Scan className="w-8 h-8" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-4">Automated Testing</h3>
                                <p className="text-slate-600">
                                    We use axe-core engine to automatically detect accessibility violations according to WCAG 2.1 AA standards.
                                </p>
                            </div>
                            <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center">
                                <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mx-auto mb-6">
                                    <FileText className="w-8 h-8" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-4">Detailed Analysis</h3>
                                <p className="text-slate-600">
                                    Each issue is categorized by severity and includes specific remediation guidance and code examples.
                                </p>
                            </div>
                            <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center">
                                <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mx-auto mb-6">
                                    <Shield className="w-8 h-8" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-4">EAA Mapping</h3>
                                <p className="text-slate-600">
                                    Results are mapped to European Accessibility Act requirements to show your compliance status.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-24 bg-slate-900 text-white">
                    <div className="max-w-4xl mx-auto px-6 text-center">
                        <h2 className="text-4xl font-bold mb-6">Need Full EAA Compliance?</h2>
                        <p className="text-xl text-slate-300 mb-10 leading-relaxed">
                            Our automated scanner is just the beginning. Get a comprehensive compliance 
                            assessment and implementation support from our accessibility experts.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link 
                                to="/contact" 
                                className="bg-white text-slate-900 px-8 py-4 rounded-xl font-semibold hover:bg-slate-100 transition-all inline-flex items-center justify-center"
                            >
                                Get Professional Audit <ArrowRight className="ml-2 w-5 h-5" />
                            </Link>
                            <Link 
                                to="/services/eaa-compliance" 
                                className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-slate-900 transition-all text-center"
                            >
                                Learn More About EAA
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </Layout>
    );
};

export default EAAComplianceScannerPage;